import {
  Component,
  Input,
  ViewEncapsulation,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  DoCheck
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-area-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  templateUrl: './area-chart.component.html',
  styleUrl: './area-chart.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AreaChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, DoCheck {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  
  @Input() areaChartData: any[] = []; // Same format as polarChartData: [{name: "Brand", series: [{name: "Metric", value: 85}]}]
  @Input() view: [number, number] = [500, 500];
  @Input() overideView: [number, number] | null = null;
  @Input() scheme: string = 'vivid';
  @Input() animations: boolean = false;
  @Input() tooltipDisabled: boolean = false;
  @Input() xAxis: boolean = true;
  @Input() yAxis: boolean = true;
  @Input() legend: boolean = false;
  @Input() legendTitle: string = 'Brands';
  @Input() showXAxisLabel: boolean = false;
  @Input() showYAxisLabel: boolean = false;
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() maxValue: number | null = null; // Optional max value for radar chart scale

  @Output() select: EventEmitter<any> = new EventEmitter();

  // Chart.js Area Chart Configuration
  public areaChartDataConfig: ChartConfiguration['data'] = {
    labels: [],
    datasets: []
  };

  public areaChartType: ChartType = 'radar';
  
  // Get chart options for radar chart
  get areaChartOptions(): ChartConfiguration['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        intersect: false,
        mode: 'point'
      },
      scales: {
        r: {
          beginAtZero: true,
          display: this.yAxis,
          min: 0,
          max: this.maxValue !== null ? this.maxValue : 80, // Use custom max if provided, else default to 80
          ticks: {
            stepSize: this.maxValue !== null ? (this.maxValue / 5) : 10, // Adjust step size based on max
            display: true,
            backdropColor: 'transparent',
            color: '#666',
            font: {
              size: 11
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
            circular: true
          },
          pointLabels: {
            display: true,
            color: '#333',
            font: {
              size: 12,
              weight: 'normal'
            },
            padding: 25
          },
          angleLines: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1
          }
        }
      },
      plugins: {
        legend: {
          display: this.legend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          enabled: !this.tooltipDisabled,
          animation: false,
          intersect: false
        }
      },
      elements: {
        point: {
          radius: 4,
          hoverRadius: 6,
          hoverBorderWidth: 2,
          hoverBorderColor: 'rgba(255, 255, 255, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'rgba(0, 0, 0, 0.8)',
          borderWidth: 2
        },
        line: {
          borderWidth: 2,
          tension: 0.1,
          fill: true // This creates the filled area
        }
      },
      onHover: (event: any, active: any) => {
        if (active && active.length > 0) {
          this.onActivate(active);
        } else {
          this.onDeactivate(event);
        }
      }
    };
  }

  // Chart.js specific methods
  private lastDataSignature: string = '';

  ngOnInit(): void {
    if (this.overideView) {
      this.view = this.overideView;
    } else {
      this.setResponsiveView();
    }
    this.updateChartData();
  }

  ngDoCheck(): void {
    const currentSig = this.buildDataSignature();
    if (currentSig !== this.lastDataSignature) {
      this.lastDataSignature = currentSig;
      this.updateChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['areaChartData']) {
      this.lastDataSignature = this.buildDataSignature();
      this.updateChartData();
    }
  }

  ngAfterViewInit(): void {
    this.updateChartData();
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
  }

  @HostListener('window:resize')
  onResize(): void {
    this.setResponsiveView();
    setTimeout(() => {
      if (this.chart && this.chart.chart) {
        this.chart.chart.resize();
        this.chart.update();
      }
    }, 100);
  }

  setResponsiveView(): void {
    const width = window.innerWidth;
    if (width < 480) {
      this.view = [250, 250];
    } else if (width < 768) {
      this.view = [320, 320];
    } else if (width < 1024) {
      this.view = [400, 400];
    } else {
      this.view = [700, 500];
    }
  }

  private updateChartData(): void {
    if (!this.areaChartData || this.areaChartData.length === 0) {
      this.areaChartDataConfig = { labels: [], datasets: [] };
      if (this.chart && this.chart.chart) {
        this.chart.update('none');
      }
      return;
    }

    // Extract labels from first series (assuming all series have same labels)
    // Format: [{name: "Brand", series: [{name: "Metric", value: 85}]}]
    const firstSeries = this.areaChartData[0];
    const labels = firstSeries?.series?.map((item: any) => item.name) || [];

    // Prepare datasets
    const datasets: any[] = [];

    // Light grey shades for backgrounds
    const lightGreyShades = [
      'rgba(230, 230, 230, 0.3)',  // Very light grey
      'rgba(220, 220, 220, 0.3)',  // Light grey
      'rgba(210, 210, 210, 0.3)',  // Medium light grey
      'rgba(200, 200, 200, 0.3)',  // Slightly darker grey
      'rgba(190, 190, 190, 0.3)',  // Medium grey
      'rgba(180, 180, 180, 0.3)'   // Darker grey
    ];

    // Colorful border colors for distinguishing brands
    const colorfulBorders = [
      'rgb(0, 0, 0)',      // Black
      'rgb(6, 121, 3)',      // Green
      'rgb(164, 84, 104)',     // Brown
      'rgb(122, 163, 229)',   // Blue
      'rgb(168, 56, 93)',     // Pink
      'rgb(255, 152, 0)',     // Orange
      'rgb(76, 175, 80)',     // Light Green
      'rgb(156, 39, 176)'     // Purple
    ];

    // Create dataset for each series in areaChartData
    this.areaChartData.forEach((seriesData, index) => {
      if (seriesData.series && Array.isArray(seriesData.series)) {
        const data = seriesData.series.map((item: any) => item.value || 0);
        const colorIndex = index % lightGreyShades.length;
        
        // Use light grey shades for background, colorful borders for distinction
        const bgColor = lightGreyShades[colorIndex];
        const borderColor = colorfulBorders[colorIndex % colorfulBorders.length];
        
        datasets.push({
          label: seriesData.name || `Brand ${index + 1}`,
          data: data,
          backgroundColor: bgColor,
          borderColor: borderColor,
          borderWidth: 2.5,
          fill: true, // This creates the filled radar area
          // tension: 0.1,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: borderColor,
          pointBorderColor: 'rgba(255, 255, 255, 1)',
          pointBorderWidth: 2
        });
      }
    });

    this.areaChartDataConfig = {
      labels: labels,
      datasets: datasets
    };

    // Update chart if it exists
    if (this.chart && this.chart.chart) {
      this.chart.update('none');
    }
  }

  private buildDataSignature(): string {
    try {
      const data = Array.isArray(this.areaChartData)
        ? this.areaChartData.map((item: any) => ({
            name: item?.name,
            series: Array.isArray(item?.series)
              ? item.series.map((s: any) => [s?.name, s?.value])
              : []
          }))
        : [];
      return JSON.stringify(data);
    } catch {
      return Math.random().toString();
    }
  }

  // Event handlers for Chart.js
  onChartSelect(event: any): void {
    this.select.emit(event);
  }

  onChartActivate(event: any): void {
    // Handle activation
  }

  onChartDeactivate(event: any): void {
    // Handle deactivation
  }

  // Legacy methods for compatibility
  onSelect(event: any): void {
    this.select.emit(event);
  }

  onActivate(event: any): void {
    // Handle activation
  }

  onDeactivate(event: any): void {
    // Handle deactivation
  }
}